import { useState } from "react";
import { TENANT_DOMAIN } from "../../../utils/domain";
import { ERROR_MESSAGES } from "../../../constant/constant";
import { useAxios } from "../../../utils/useAxios";
import { useAuth } from "../../../components/context/LoginContext/AuthContext";

/**
 * @typedef {object} AnnouncementMedia
 * @property {string} url - Media file name
 */

/**
 * @typedef {object} AnnouncementDetail
 * @property {number} id - Notification ID
 * @property {string} scope - Notification scope (e.g. "tenant")
 * @property {string} tenant_id - Tenant UUID
 * @property {number} institution_id - Institution ID
 * @property {number} created_by - ID of the user who created the notification
 * @property {string} created_by_role - Role of the creator (e.g. "organization-admin")
 * @property {number} receiver_id - Receiver ID
 * @property {string} receiver_type - Receiver model type (e.g. "App\\Models\\User")
 * @property {string} title - Notification title
 * @property {string} description - Notification description/content
 * @property {string} category - Notification category (e.g. "manual")
 * @property {string} event_key - Event key (e.g. "announcement")
 * @property {number} batch_id - Batch ID for the notification group
 * @property {number|null} reference_id - Reference ID
 * @property {string|null} reference_type - Reference model type
 * @property {AnnouncementMedia[]|null} media_url - Attached media files
 * @property {string|null} href - Link URL
 * @property {*} data - Additional data
 * @property {string|null} seen_at - Seen timestamp
 * @property {string|null} read_at - Read timestamp
 * @property {string|null} dismissed_at - Dismissed timestamp
 * @property {string} created_at - Created timestamp
 * @property {string} updated_at - Updated timestamp
 */

/**
 * Custom hook to fetch announcement detail by notification ID
 */

export const useGetAnnouncementsDetail = () => {
  const [announcement, setAnnouncement] = useState(undefined);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const { axiosInstance } = useAxios();
  const { fetchWithAuth } = useAuth();
  const apiUrl = process.env.REACT_APP_API_URL;

  const getAnnouncementDetail = async (notificationId, type) => { 
    try {
      setLoading(true);
      setError("");

      const domain = type === "pcnormal" ? apiUrl : TENANT_DOMAIN;
      const url = `${domain}/notification/${notificationId}/show`;

      if (type === "pcnormal") {
        const raw = await fetchWithAuth(url, { method: "GET" });
        const parsed = await raw.json();
        setAnnouncement(parsed?.data || null);
        return parsed?.data || null;
      }

      // pnormal
      const response = await axiosInstance.get(url);
      const data = response.data?.data || null;
      setAnnouncement(data);
      return data;

    } catch (err) {
      setAnnouncement(null);
      if (err?.response?.status === 500) {
        setError(ERROR_MESSAGES.INTERNAL_SERVER);
      } else {
        setError(err?.response?.data?.message || ERROR_MESSAGES.UNKNOWN);
      }
    } finally {
      setLoading(false);
    }
  };

  return { announcement, loading, error, getAnnouncementDetail, setAnnouncement };
};
